import { CURRENT_WORKSPACE_ID_STORAGE_KEY } from "@/src/utils/constants"

// ----------------------------------------------------------------------
// Helper function to get workspace ID from localStorage
// ----------------------------------------------------------------------

const getWorkspaceId = () => localStorage.getItem(CURRENT_WORKSPACE_ID_STORAGE_KEY)

// ----------------------------------------------------------------------
// API URLs
// All workspace-dependent URLs are functions that inject the workspace ID
// Static URLs are simple strings
// ----------------------------------------------------------------------

const apiUrls = {
    // Config
    APP_CONFIG: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/config/`
    },

    // Auth - Static URLs
    AUTH_ME: "/users/me",
    WORKSPACE_ME: (workspaceId: string) => `/workspace/${workspaceId}/me`,
    AUTH_SIGN_IN: "/auth/login",
    AUTH_SIGN_UP: "/auth/signup",
    AUTH_REFRESH_TOKEN: "/auth/refresh-token",
    AUTH_GOOGLE_LOGIN: "/auth/google/login",
    AUTH_MS_LOGIN: "/auth/microsoft/login",
    AUTH_USERS: "/users/all",

    //ATS
    AUTH_V2_PARTNER_CANDIDATE_VALIDATE_HASH: "/auth/v2/partner-candidate/validate-hash",
    AUTH_V2_PARTNER_CANDIDATE_SEND_OTP: "/auth/v2/partner-candidate/send-otp",
    AUTH_V2_PARTNER_CANDIDATE_VERIFY_OTP: "/auth/v2/partner-candidate/verify-otp",

    // Users
    USERS_LIST: "/users/company-users",
    USER_ADD: "/users/create",
    USERS_ADD: () => "/users/add", // Alias for USER_ADD
    USER_EDIT: "/users",
    USERS_EDIT: (userId: string) => `/users/${userId}/edit`, // Alias for USER_EDIT
    USER_UPDATE: "/users/update",
    USERS_UPDATE: (userId: string) => `/users/${userId}`, // Alias for USER_UPDATE
    USERS_MANAGERS: "/users/company/managers",
    USERS_MANAGER_LIST: () => "/users/managers", // Alias for USERS_MANAGERS
    USERS_ROLE_PERMISSION: "/admin/permissions",
    USERS_ROLE_ASSIGN: "/admin/assign-permission",
    USERS_DELETE_PERMISSION: "/admin/remove-permission",
    USERS_ALL_ROLES: "/admin/roles",

    //ADMIN
    ADMIN_ME: () => {
        return `/internal/me`
    },

    ADMIN_WORKSPACES: () => {
        return `/internal/workspaces`
    },

    ADMIN_ALL_USERS: () => {
        return `/internal/users`
    },

    ADMIN_WORKSPACE_USERS: (workspaceId: string) => {
        return `/internal/workspaces/${workspaceId}/users`
    },

    ADMIN_LINK_USER_TO_WORKSPACE: (workspaceId: string | null) => {
        return `/internal/workspaces/${workspaceId}/users/add`
    },

    ADMIN_DELETE_USER: (workspaceId: string, userId: string) => {
        return `/internal/workspaces/${workspaceId}/users/${userId}`
    },

    ADMIN_EDIT_USER: (workspaceId: string, userId: string) => {
        return `/internal/workspaces/${workspaceId}/users/${userId}`
    },

    ADMIN_TEAM_MANAGERS: (workspaceId: string) => {
        return `/workspace/${workspaceId}/team/managers`
    },

    ADMIN_TEAM_RECRUITERS: (workspaceId: string) => {
        return `/workspace/${workspaceId}/team/recruiters`
    },

    ADMIN_ASSIGN_RECRUITERS_TO_MANAGER: (workspaceId: string) => {
        return `/workspace/${workspaceId}/team/recruiters/assign-manager`
    },

    ADMIN_UPDATE_CREDITS: (workspaceId: string) => {
        return `/internal/workspaces/${workspaceId}/credits`
    },

    ADMIN_WORKSPACE_OUTBOUND_AUDIO: (workspaceId: string) => {
        return `/internal/workspaces/${workspaceId}/outbound-audio`
    },

    ADMIN_WORKSPACE_GENERATE_CSV_COLUMN_CATALOG: (workspaceId: string) => {
        return `/internal/workspaces/${workspaceId}/generate-csv-column-catalog`
    },

    ADMIN_WORKSPACE_INTERVIEW_SCHEDULING: (workspaceId: string) => {
        return `/internal/workspaces/${workspaceId}/interview-scheduling`
    },

    ADMIN_LIST_JOBS: () => {
        return `/internal/jobs`
    },

    ADMIN_GET_JOB_LEADS: (jobId: string) => {
        return `/internal/jobs/${jobId}`
    },

    ADMIN_JOB_ASSIGNEES: (jobId: string) => {
        return `/internal/jobs/${jobId}/assignees`
    },

    ADMIN_DELETE_JOB_LEADS: (workspaceId: string) => {
        return `/internal/workspace/${workspaceId}/jobs/candidates/bulk-delete`
    },

    /** Internal: lookup OTP for a phone number (platform tooling). */
    ADMIN_LOOKUP_OTP: "/internal/lookup-otp",

    ADMIN_PROMPT_POLICIES: "/internal/prompt-policies",

    ADMIN_UPDATE_PROMPT_POLICY: (policyName: string) => `/internal/prompt-policies/by-name/${policyName}`,

    ADMIN_PROMPT_POLICY_VERSIONS: (policyName: string) =>
        `/internal/prompt-policies/by-name/${policyName}/versions`,

    ADMIN_PROMPT_POLICY_REVERT: (policyName: string, versionNumber: number) =>
        `/internal/prompt-policies/by-name/${policyName}/revert/${versionNumber}`,

    ADMIN_PROMPT_POLICY_APPLY_GLOBAL: (policyName: string) =>
        `/internal/prompt-policies/by-name/${policyName}/apply-global`,

    ADMIN_PROMPT_POLICY_WORKSPACE_OVERRIDES: (policyName: string) =>
        `/internal/prompt-policies/by-name/${policyName}/workspace-overrides`,

    ADMIN_PROMPT_POLICY_DELETE_WORKSPACE_OVERRIDE: (policyName: string, workspaceId: string) =>
        `/internal/prompt-policies/by-name/${policyName}/workspace-overrides/${workspaceId}`,

    WORKSPACE_MONETIZATION: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/credits/monetization`
    },
    /** Feature toggles only (no credit balances) — requires `viewAgent`. */
    WORKSPACE_MONETIZATION_FEATURES: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/credits/monetization/features`
    },
    /** @deprecated Use WORKSPACE_MONETIZATION or WORKSPACE_MONETIZATION_FEATURES */
    WORKSPACE_TOGGLES: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/credits/monetization`
    },
    // Mandates (Jobs) - Dynamic URLs
    MANDATES_LIST: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/all`
    },
    MANDATES_DROPDOWN: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/dropdown`
    },
    MANDATE_DETAILS: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/${jobId}`
    },
    MANDATE_JOB_DETAILS: "/mandates/apna-job-details",
    MANDATE_DETAILS_PUBLIC: (jobId: string) => `/public/job/${jobId}`,
    MANDATE_UPDATE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs`
    },
    MANDATE_DRAFT: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/draft`
    },
    MANDATE_ACTIVATE: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/${jobId}/activate`
    },
    MANDATE_GENERATE_SHORT_LINK: (mandateId: string) => `/mandates/${mandateId}/generate-shortio-link`,
    MANDATE_AI_RECRUITER_TYPE: "/mandates/agents",
    MANDATE_AI_RECRUITER_TYPE_FOR_MANDATE: (mandateId: string) => `/mandates/${mandateId}/ai-recruiter-type`,
    MANDATE_GENERATE_JD_TEXT: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/generate-job-description`
    },
    MANDATE_GENERATE_JD_FILE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/upload-parse-job-description`
    },
    MANDATE_GENERATE_DUMMY_CANDIDATE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/create-dummy`
    },
    MANDATE_DELETE_ROUND: (roundId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/rounds/${roundId}`
    },
    MANDATE_ENABLE_AI: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/${jobId}/enable-ai`
    },

    MANDATE_DEACTIVATE: (mandateId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/${mandateId}/de-activate`
    },

    MANDATE_DUPLICATE: (mandateId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/${mandateId}/duplicate`
    },

    // Job Rounds
    JOB_ROUND_CREATE: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/rounds/draft/all?jobId=${jobId}`
    },
    JOB_ROUND_GET_BY_JOB: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/rounds/all`
    },
    JOB_ROUND_UPDATE: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/rounds/all?jobId=${jobId}`
    },
    JOB_ROUND_BY_ID: (roundId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/rounds/${roundId}`
    },
    JOB_SINGLE_ROUND_GENERATE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/rounds/generate-single`
    },
    JOB_SINGLE_INTERVIEW_SCHEDULING_ROUND_GENERATE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/rounds/generate-single/interview-scheduling`
    },
    JOB_ROUND_PROMPT_GENERATION: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/${jobId}/generateScript`
    },
    JOB_ROUND_GET_PROMPT: (roundId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/rounds/${roundId}/call-guidelines`
    },
    JOB_ROUND_ENRICH_ALL: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/rounds/enrich/all?jobId=${jobId}`
    },
    JOB_ROUND_QUESTION_BANKS: (roundId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/rounds/${roundId}/question-banks`
    },
    JOB_PUBLISH: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/${jobId}/publish`
    },

    // Question Banks (workspace-scoped)
    QUESTION_BANKS_LIST: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/question-banks`
    },
    QUESTION_BANKS_CREATE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/question-banks`
    },
    QUESTION_BANKS_UPLOAD_CREATE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/question-banks/upload`
    },
    QUESTION_BANK_BY_ID: (questionBankId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/question-banks/${questionBankId}`
    },
    QUESTION_BANK_JOBS: (questionBankId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/question-banks/${questionBankId}/jobs`
    },
    QUESTION_BANK_UPLOAD_REPLACE: (questionBankId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/question-banks/${questionBankId}/upload`
    },

    // Candidates (Leads)
    CANDIDATES_LIST: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/all`
    },
    CANDIDATES_COUNT_BY_STATUS: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/count-by-status?jobId=${jobId}`
    },
    CANDIDATE_DETAILS: (candidateId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/${candidateId}/details`
    },
    CANDIDATE_STATUS_UPDATE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates`
    },
    CANDIDATE_UPDATE: (candidateId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/${candidateId}`
    },
    CANDIDATE_CREATE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/create`
    },
    CANDIDATE_CALLBACK: "/leads/schedule-callback",
    CANDIDATE_EXPORT: "/bulk-jobs",
    CANDIDATE_RE_EVALUATE: (leadId: string) => `/lead-scoring/re-evaluate-lead/${leadId}`,
    LEADS_PUBLIC_AI_STATUS: "/public/leads/ai-status",
    CANDIDATE_MOVE_NEXT_ROUND: (candidateId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/${candidateId}/move-to-next-round`
    },
    CANDIDATE_REJECT: (candidateId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/${candidateId}/reject`
    },
    CANDIDATE_REATTEMPT_ROUND: (candidateId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/${candidateId}/reattempt-round`
    },
    CANDIDATE_PROCTORING_INSIGHTS: (vendorCallId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/proctoring-events/${vendorCallId}`
    },
    CANDIDATE_PARSE_RESUME: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/parse-resume`
    },
    CANDIDATE_REEVALUATE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/retrigger-evaluation`
    },
    EXPORT_CANDIDATES: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/export?jobId=${jobId}`
    },
    CANDIDATE_ADD_NOTE: (candidateId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/${candidateId}/note`
    },
    CANDIDATE_RETAKE_ROUND: (candidateRoundId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidate-rounds/${candidateRoundId}/retakes`
    },
    /** POST `{ jobId, candidateId, roundId }` — kicks off async PDF generation; poll `PDF_SIGNED_URL` until `data.signedUrl` is present. */
    PDF_GENERATE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/pdf/generate`
    },
    /** GET — query: `jobId`, `candidateId`. Returns `data.signedUrl` when the PDF is ready. */
    PDF_SIGNED_URL: (jobId: string, candidateId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/pdf/signed-url?jobId=${encodeURIComponent(jobId)}&candidateId=${encodeURIComponent(candidateId)}`
    },

    // Public Candidates
    CANDIDATES_PUBLIC_CREATE: "/public-leads/job-link",
    CANDIDATES_PUBLIC_CREATE_V2: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/walk-in`
    },
    CANDIDATES_PUBLIC_AI_STATUS: "/public-leads/get-ai-status",
    CANDIDATE_PUBLIC_PHONE_CALL: (leadId: string) => `/public-leads/${leadId}/trigger-outbound-call`,
    PUBLIC_INTERVIEW_SCHEDULING_RESCHEDULE: (rescheduleHash: string) =>
        `/public/interview-scheduling/reschedule/${rescheduleHash}`,

    // Clients
    CLIENTS_LIST: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/clients`
    },
    CLIENT_DETAILS: (clientId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/clients/${clientId}`
    },
    CLIENT_FORM: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/clients`
    },
    CANDIDATES_ME: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/candidates/candidate/me?jobId=${jobId}`
    },

    // Bulk Upload (outbound: request body { jobId, roundId, fileUrl }; roundId required when round has outbound enabled)
    BULK_UPLOAD_LIST: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/bulk-jobs/all`
    },
    BULK_UPLOAD_LEADS: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/bulk-jobs`
    },
    BULK_UPLOAD_JOB: (jobId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/bulk-jobs/${jobId}`
    },
    CAMPAIGN_START: (jobId: string, roundId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/${jobId}/rounds/${roundId}/campaign/start`
    },
    CAMPAIGN_END: (jobId: string, roundId: string) => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/${jobId}/rounds/${roundId}/campaign/end`
    },
    BULK_UPLOAD_PRIVATE_FILE: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/config/signed-url/file-upload`
    },
    BULK_UPLOAD_PUBLIC_RESUME: "/public/signed-url/file-upload",
    BULK_UPLOAD_FILE_DOWNLOAD: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/config/signed-url/file-download-from-url`
    },

    // Personal WhatsApp
    WHATSAPP_STATUS: "/personal-whatsapp/status",
    WHATSAPP_CONNECTION_STATUS: () => "/whatsapp/connection-status",
    WHATSAPP_WHITELIST: "/personal-whatsapp/whitelist",
    WHATSAPP_CONNECT: "/personal-whatsapp/connect",
    WHATSAPP_DISCONNECT: "/personal-whatsapp/disconnect",
    WHATSAPP_TEMPLATE: "/personal-whatsapp/template",
    WHATSAPP_GET_TEMPLATE: () => "/whatsapp/templates",
    WHATSAPP_SEND_MESSAGE: "/personal-whatsapp/send-message",

    // Selections
    SELECTIONS_LIST: "/selections/all",
    SELECTION_DETAILS: "http://localhost:3000/selection",
    SELECTION_STATUS_UPDATE: "/selections",
    SELECTION_FORM: "/selections",

    // Interviews
    INTERVIEWS_LIST: "/interviews/all",
    INTERVIEWS_CREATE: () => "/interviews",
    INTERVIEW_DETAILS: (interviewId: string) => `/interviews/${interviewId}`,
    INTERVIEW_POST: "/interviews",
    INTERVIEW_POST_BULK: "/interviews/bulk",
    INTERVIEW_UPDATE: "/interviews",
    INTERVIEW_ROUND_UPDATE: "/interviews",

    // Credits
    CREDITS_LIST: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/credits-ledger`
    },

    CREDITS_SUMMARY: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/credits/summary`
    },

    CREDITS_HISTORY: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/credits/history`
    },

    // OTP
    OTP_VERIFY: "/auth/verify-otp",
    OTP_SEND: "/auth/send-otp",

    // File Upload
    FILE_UPLOAD: "/leads/parse/resume",
    FILE_PARSE_RESUME: () => "/parse-resume",
    FILE_GENERIC_UPLOAD: (fileType: string) => `/leads/upload/file?fileType=${fileType}`,
    FILE_PARSE_RESUME_PUBLIC: "/public/parse/resume",
    FILE_PARSE_RESUME_PUBLIC_CANDIDATES: "/public/candidates/parse-resume",
    BULK_UPLOAD_RESUME_PUBLIC: () => "/bulk-upload/resume-public",

    // Company
    COMPANY_UPDATE_LOGO: "/companies/update-logo",
    WORKSPACE_CREATE: "/admin/create-workspace",

    // Job Titles / Suggester
    JOB_TITLE_LIST: "/suggester?size=20",

    // AI
    AI_EXTRACT_JOB_DETAILS: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/jobs/extract-job-detail`
    },

    // Onboarding
    ONBOARDING_STEPS: "/users/onboarding-steps-completion",
    ONBOARDING_COMPLETED: "/users/onboarding-completed",
    ONBOARDING_COMPLETE: () => "/users/onboarding-completed", // Alias for ONBOARDING_COMPLETED

    // Database Saved Searches
    DB_SAVED_SEARCHES_LIST: "/bulk-jobs/magic-jobs/get-saved-searches",
    DB_SAVED_SEARCHES_COUNT: "/bulk-jobs/magic-jobs/get-saved-search-count",

    // Agents
    AGENTS_LIST: () => {
        const workspaceId = getWorkspaceId()
        return `/workspace/${workspaceId}/ai-recruiters`
    },

    // Recruiters
    RECRUITERS_GET: "/leads/all",
}

export default apiUrls
